import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import deepEquals from "fast-deep-equal";
import React from "react";
import { recurrenceTypes } from "../DateTimeRange/recurrenceTypes";
import { formatDate } from "../Utilities/DateHelper";
import { getImage } from "../Utilities/FileHelper";
import { hasSpecialCharacters, isNullOrWhiteSpace, isValidEmail } from "../Utilities/StringHelper";
import validationPropTypes from "./Validations";
import { DEFAULT_FORMATS } from "../DateTimePicker";
const prefix = {
  from: "from",
  to: "to"
};
const shouldValidateImagePixelDimensions = file => {
  let shouldValidate = false;
  if (file.isImage && file.fileExtension !== "svg") {
    shouldValidate = true;
  }
  return shouldValidate;
};
export const validationNames = {
  required: "required",
  requiredArray: "requiredArray",
  regExpPattern: "regExpPattern",
  minLength: "minLength",
  maxLength: "maxLength",
  minValue: "minValue",
  maxValue: "maxValue",
  validDateFormat: "validDateFormat",
  minValueDateValidation: "minValueDateValidation",
  maxValueDateValidation: "maxValueDateValidation",
  rangeRequired: "RangeRequired",
  fromRangeRequired: `${prefix.from}RangeRequired`,
  toRangeRequired: `${prefix.to}RangeRequired`,
  numberRangeMinValue: "numberRangeMinValue",
  fromNumberRangeMinValue: `${prefix.from}NumberRangeMinValue`,
  toNumberRangeMinValue: `${prefix.to}NumberRangeMinValue`,
  numberRangeMaxValue: "numberRangeMaxValue",
  fromNumberRangeMaxValue: `${prefix.from}NumberRangeMaxValue`,
  toNumberRangeMaxValue: `${prefix.to}NumberRangeMaxValue`,
  numberRangeBoundary: "numberRangeBoundary",
  datesValidRangeValidation: "datesValidRangeValidation",
  fromValidDateRangeValidation: `${prefix.from}validDateRangeValidation`,
  toValidDateRangeValidation: `${prefix.to}validDateRangeValidation`,
  dateRangeBoundary: "dateRangeBoundary",
  dateRangeMinValueValidation: "dateRangeMinValueValidation",
  fromDateRangeMinValueValidation: `${prefix.from}DateRangeMinValueValidation`,
  toDateRangeMinValueValidation: `${prefix.to}DateRangeMinValueValidation`,
  dateRangeMaxValueValidation: "dateRangeMaxValueValidation",
  fromDateRangeMaxValueValidation: `${prefix.from}DateRangeMaxValueValidation`,
  toDateRangeMaxValueValidation: `${prefix.to}DateRangeMaxValueValidation`,
  imageMaxHeightValidation: "imageMaxHeightValidation",
  imageMinHeightValidation: "imageMinHeightValidation",
  imageMaxWidthValidation: "imageMaxWidthValidation",
  imageMinWidthValidation: "imageMinWidthValidation",
  maxSizeValidation: "maxSizeValidation",
  minSizeValidation: "minSizeValidation",
  imageAspectRatioValidation: "imageAspectRatioValidation",
  mustBeImageValidation: "mustBeImageValidation",
  rejectedFilesValidation: "rejectedFilesValidation",
  referenceMaxItems: "referenceMaxItems",
  referenceMinItems: "referenceMinItems",
  allowedExtensionsValidation: "allowedExtensionsValidation",
  childComponentValidation: "childComponentValidation",
  wrappedFieldRendererValidation: "wrappedFieldRendererValidation",
  emailValidation: "emailValidation",
  specialCharactersValidation: "specialCharactersValidation",
  isRegExpValidation: "isRegExpValidation",
  recurrenceValidation: "recurrenceValidation"
};
export const errorMessage = (validation, errorMessages, defaultMessage) => {
  const customMessage = errorMessages ? errorMessages[validation] : undefined;
  return customMessage || defaultMessage;
};
export const requiredValidation = (value, props, component) => {
  const requiredValue = typeof value === "undefined" || value === null || value === "" || (Array.isArray(value) ? value?.length === 0 : false);
  const result = {
    error: requiredValue,
    message: errorMessage(validationNames.required, props.errorMessages, "This field cannot be blank."),
    validationName: validationNames.required
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const requiredArrayValidation = (value, props, component) => {
  const result = {
    error: value === undefined || value === null || value.length === 0,
    message: errorMessage(validationNames.requiredArray, props.errorMessages, "This field cannot be blank."),
    validationName: validationNames.requiredArray
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const isRegExpValidation = (value, props, component) => {
  let isRegex = true;
  try {
    new RegExp(value);
  } catch {
    isRegex = false;
  }
  const result = {
    error: !isRegex,
    message: errorMessage(validationNames.isRegExpValidation, props.errorMessages, "This field must be a valid regular expression."),
    validationName: validationNames.isRegExpValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const regExpValidation = (value, props, component) => {
  const {
    regExpPattern
  } = props;
  if (regExpPattern === undefined) {
    return Promise.resolve({
      error: false,
      message: undefined,
      validationName: validationNames.regExpPattern
    });
  }
  const result = {
    error: value && !isNullOrWhiteSpace(regExpPattern) ? !RegExp(regExpPattern).test(value) : false,
    message: errorMessage(validationNames.regExpPattern, props.errorMessages, "This field did not match the regular expression provided."),
    validationName: validationNames.regExpPattern
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const minLengthValidation = (value, props, component) => {
  const {
    minLength
  } = props;
  const minLengthValue = props["data-minLength"] ? parseFloat(props["data-minLength"]) : minLength;
  const result = {
    error: minLengthValue !== undefined && Boolean(value) && minLengthValue > value.length,
    message: errorMessage(validationNames.minLength, props.errorMessages, `${value?.length} does not meet the minimum length of ${minLengthValue}.`),
    validationName: validationNames.minLength
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const maxLengthValidation = (value, props, component) => {
  const {
    maxLength
  } = props;
  const maxLengthValue = props["data-maxLength"] ? parseFloat(props["data-maxLength"]) : maxLength;
  const result = {
    error: maxLengthValue !== undefined && (maxLengthValue < value?.length ?? 0),
    message: errorMessage(validationNames.maxLength, props.errorMessages, `${value?.length} does not meet the maximum length of ${maxLengthValue}.`),
    validationName: validationNames.maxLength
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const minValueValidation = (value, props, component) => {
  const {
    minValue
  } = props;
  if (minValue === undefined) {
    return Promise.resolve({
      error: false,
      message: undefined,
      validationName: validationNames.minValue
    });
  }
  const result = {
    error: value ? minValue > value : false,
    message: errorMessage(validationNames.minValue, props.errorMessages, `A minimum value of ${minValue} is required.`),
    validationName: validationNames.minValue
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const maxValueValidation = (value, props, component) => {
  const {
    maxValue
  } = props;
  if (maxValue === undefined) {
    return Promise.resolve({
      error: false,
      message: undefined,
      validationName: validationNames.validDateFormat
    });
  }
  const result = {
    error: value ? maxValue < value : false,
    message: errorMessage(validationNames.maxValue, props.errorMessages, `The maximum value allowed is ${maxValue}.`),
    validationName: validationNames.maxValue
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const validDateValidation = (value, props, component) => {
  if (value) {
    const parsedDate = Date.parse(value);
    const timeUnit = props && props.pickerType === "time" ? "time" : "date";
    const result = {
      error: isNaN(parsedDate),
      message: errorMessage(validationNames.validDateFormat, props.errorMessages, `Please enter a valid ${timeUnit}.`),
      validationName: validationNames.validDateFormat
    };
    return result.error ? Promise.reject(result) : Promise.resolve(result);
  }
  return Promise.resolve({
    error: false,
    message: undefined,
    validationName: validationNames.validDateFormat
  });
};
export const minValueDateValidation = (value, props) => {
  const {
    minValue,
    format = DEFAULT_FORMATS["date"]
  } = props;
  if (minValue === undefined) {
    return Promise.resolve({
      error: false,
      message: undefined,
      validationName: validationNames.minValueDateValidation
    });
  }
  const parsedDate = Date.parse(value);
  const parsedMinValue = Date.parse(minValue);
  const result = {
    error: Boolean(parsedMinValue) && parsedDate < parsedMinValue,
    message: errorMessage(validationNames.minValueDateValidation, props.errorMessages, `Date must be after, ${formatDate(minValue, format)}`),
    validationName: validationNames.minValueDateValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const maxValueDateValidation = (value, props) => {
  const {
    maxValue,
    format = DEFAULT_FORMATS["date"]
  } = props;
  if (maxValue === undefined) {
    return Promise.resolve({
      error: false,
      message: undefined,
      validationName: validationNames.maxValueDateValidation
    });
  }
  const parsedDate = Date.parse(value);
  const parsedMaxValue = Date.parse(maxValue ?? "0");
  const result = {
    error: Boolean(parsedMaxValue) && parsedDate > parsedMaxValue,
    message: errorMessage(validationNames.maxValueDateValidation, props.errorMessages, `Date must be before, ${formatDate(maxValue, format)}`),
    validationName: validationNames.maxValueDateValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const numberRangeMinValue = async (value, props, component) => {
  const {
    from,
    to
  } = getValue(props, component);
  const {
    errorMessages,
    fromProps,
    toProps
  } = props;
  const fromResult = await minValueValidation(from, props, component).catch(result => result);
  const toResult = await minValueValidation(to, props, component).catch(result => result);
  if (fromResult.error && toResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.numberRangeMinValue, errorMessages, "This field are less than the minimum value allowed."),
      messageTo: errorMessage(validationNames.numberRangeMinValue, errorMessages, "This field are less than the minimum value allowed."),
      validationName: validationNames.numberRangeMinValue
    });
  } else if (fromResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.fromNumberRangeMinValue, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} is less than the minimum value of ${props.minValue}.`),
      validationName: validationNames.fromNumberRangeMinValue
    });
  } else if (toResult.error) {
    return Promise.reject({
      error: true,
      messageTo: errorMessage(validationNames.toNumberRangeMinValue, errorMessages, `${toProps && toProps.label ? toProps.label : "To field"} is less than the minimum value of ${props.minValue}.`),
      validationName: validationNames.toNumberRangeMinValue
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.numberRangeMinValue
    });
  }
};
export const numberRangeMaxValue = async (value, props, component) => {
  const {
    from,
    to
  } = getValue(props, component);
  const {
    errorMessages,
    fromProps,
    toProps
  } = props;
  const fromResult = await maxValueValidation(from, props, component).catch(result => result);
  const toResult = await maxValueValidation(to, props, component).catch(result => result);
  if (fromResult.error && toResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.numberRangeMaxValue, errorMessages, "This field are greater than the maximum value allowed."),
      messageTo: errorMessage(validationNames.numberRangeMaxValue, errorMessages, "This field are greater than the maximum value allowed."),
      validationName: validationNames.numberRangeMaxValue
    });
  } else if (fromResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.fromNumberRangeMaxValue, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} is greater than the maximum value of ${props.maxValue}.`),
      validationName: validationNames.fromNumberRangeMaxValue
    });
  } else if (toResult.error) {
    return Promise.reject({
      error: true,
      messageTo: errorMessage(validationNames.toNumberRangeMaxValue, errorMessages, `${toProps && toProps.label ? toProps.label : "To field"} is greater than the maximum value of ${props.maxValue}.`),
      validationName: validationNames.toNumberRangeMaxValue
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.numberRangeMaxValue
    });
  }
};
export const rangeRequiredValidation = async (value, props, component) => {
  const {
    from,
    to
  } = value;
  const {
    errorMessages,
    fromProps,
    toProps,
    required
  } = props;
  if (!required && !from && !to) return Promise.resolve({
    error: false,
    messagesFrom: "",
    messagesTo: "",
    validationName: validationNames.rangeRequired
  });
  const fromResult = await requiredValidation(from, props, component).catch(result => result);
  const toResult = await requiredValidation(to, props, component).catch(result => result);
  if (fromResult.error && toResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.rangeRequired, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} cannot be blank.`),
      messageTo: errorMessage(validationNames.rangeRequired, errorMessages, `${toProps && toProps.label ? toProps.label : "To field"} cannot be blank.`),
      validationName: validationNames.rangeRequired
    });
  } else if (fromResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.fromRangeRequired, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} cannot be blank.`),
      validationName: validationNames.fromRangeRequired
    });
  } else if (toResult.error) {
    return Promise.reject({
      error: true,
      messageTo: errorMessage(validationNames.toRangeRequired, errorMessages, `${toProps && toProps.label ? toProps.label : "To field"} cannot be blank.`),
      validationName: validationNames.toRangeRequired
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.rangeRequired
    });
  }
};
export const numberRangeBoundaryValidation = async (value, props, component) => {
  const {
    from,
    to
  } = getValue(props, component);
  const {
    errorMessages,
    fromProps,
    toProps
  } = props;
  if (from && to && Number(from) > Number(to)) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.numberRangeBoundary, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} cannot be greater than ${toProps && toProps.label ? toProps.label : "To field"}.`),
      validationName: validationNames.numberRangeBoundary
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.numberRangeBoundary
    });
  }
};
export const dateRangeMinValueValidation = async (value, props) => {
  const {
    from,
    to
  } = value;
  const {
    errorMessages,
    fromProps,
    toProps,
    minValue,
    format = DEFAULT_FORMATS["date"]
  } = props;
  const fromResult = await minValueDateValidation(from, props).catch(result => result);
  const toResult = await minValueDateValidation(to, props).catch(result => result);
  const formattedMinValue = minValue ? formatDate(minValue, format) : "";
  if (fromResult.error && toResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.dateRangeMinValueValidation, errorMessages, `Fields cannot be before ${formattedMinValue}`),
      messageTo: errorMessage(validationNames.dateRangeMinValueValidation, errorMessages, `Fields cannot be before ${formattedMinValue}`),
      validationName: validationNames.dateRangeMinValueValidation
    });
  } else if (fromResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.fromDateRangeMinValueValidation, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} cannot be before ${formattedMinValue}.`),
      validationName: validationNames.fromDateRangeMinValueValidation
    });
  } else if (toResult.error) {
    return Promise.reject({
      error: true,
      messageTo: errorMessage(validationNames.toDateRangeMinValueValidation, errorMessages, `${toProps && toProps.label ? toProps.label : "To field"} cannot be before ${formattedMinValue}.`),
      validationName: validationNames.fromDateRangeMinValueValidation
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.dateRangeMinValueValidation
    });
  }
};
export const dateRangeMaxValueValidation = async (value, props) => {
  const {
    from,
    to
  } = value;
  const {
    errorMessages,
    fromProps,
    toProps,
    maxValue,
    format = DEFAULT_FORMATS["date"]
  } = props;
  const fromResult = await maxValueDateValidation(from, props).catch(result => result);
  const toResult = await maxValueDateValidation(to, props).catch(result => result);
  const formattedMaxDate = maxValue ? formatDate(maxValue, format) : "";
  if (fromResult.error && toResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.dateRangeMaxValueValidation, errorMessages, `Fields cannot be after ${formattedMaxDate}`),
      messageTo: errorMessage(validationNames.dateRangeMaxValueValidation, errorMessages, `Fields cannot be after ${formattedMaxDate}`),
      validationName: validationNames.dateRangeMaxValueValidation
    });
  } else if (fromResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.fromDateRangeMaxValueValidation, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} cannot be after ${formattedMaxDate}.`),
      validationName: validationNames.fromDateRangeMaxValueValidation
    });
  } else if (toResult.error) {
    return Promise.reject({
      error: true,
      messageTo: errorMessage(validationNames.toDateRangeMaxValueValidation, errorMessages, `${toProps && toProps.label ? toProps.label : "To field"} cannot be after ${formattedMaxDate}.`),
      validationName: validationNames.toDateRangeMaxValueValidation
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.dateRangeMaxValueValidation
    });
  }
};
export const dateRangeValidDateValidation = async (value, props, component) => {
  const {
    from,
    to
  } = value;
  const {
    errorMessages,
    fromProps,
    toProps
  } = props;
  const fromResult = await validDateValidation(from, props, component).catch(result => result);
  const toResult = await validDateValidation(to, props, component).catch(result => result);
  if (fromResult.error && toResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.datesValidRangeValidation, errorMessages, "This field requires a valid date format."),
      messageTo: errorMessage(validationNames.datesValidRangeValidation, errorMessages, "This field requires a valid date format."),
      validationName: validationNames.datesValidRangeValidation
    });
  } else if (fromResult.error) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.fromValidDateRangeValidation, errorMessages, `${fromProps && fromProps.label ? fromProps.label : "From field"} requires a valid date format.`),
      validationName: validationNames.fromValidDateRangeValidation
    });
  } else if (toResult.error) {
    return Promise.reject({
      error: true,
      messageTo: errorMessage(validationNames.toValidDateRangeValidation, errorMessages, `${toProps && toProps.label ? toProps.label : "To field"} requires a valid date format.`),
      validationName: validationNames.toValidDateRangeValidation
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.datesValidRangeValidation
    });
  }
};
export const dateRangeBoundaryValidation = async (value, props, component) => {
  const {
    from,
    to
  } = value;
  const {
    errorMessages,
    fromProps,
    toProps
  } = props;
  const fromResult = Date.parse(from);
  const toResult = Date.parse(to);
  if (fromResult > toResult) {
    return Promise.reject({
      error: true,
      messageFrom: errorMessage(validationNames.dateRangeBoundary, errorMessages, `The ${fromProps && fromProps.label ? fromProps.label : "From field"} must be before the ${toProps && toProps.label ? toProps.label : "To field"}.`),
      validationName: validationNames.dateRangeBoundary
    });
  } else {
    return Promise.resolve({
      error: false,
      messageFrom: "",
      messageTo: "",
      validationName: validationNames.dateRangeBoundary
    });
  }
};
export const imageMinWidthValidation = async (value, props, component) => {
  const minWidth = props.minWidth;
  const badFiles = [];
  if (minWidth) {
    for (const file of value) {
      if (shouldValidateImagePixelDimensions(file)) {
        if (file.pixelWidth) {
          if (file.pixelWidth < minWidth) badFiles.push(`"${file.name}"`);
        } else {
          const dim = await getImage(file);
          if (dim.width < minWidth) {
            badFiles.push(`"${file.name}"`);
          }
        }
      }
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.imageMinWidthValidation, props.errorMessages, `Width of ${badFiles.join(", ")} must be at least ${minWidth}px.`),
    validationName: validationNames.imageMinWidthValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const imageMaxWidthValidation = async (value, props, component) => {
  const maxWidth = props.maxWidth;
  const badFiles = [];
  if (maxWidth) {
    for (const file of value) {
      if (shouldValidateImagePixelDimensions(file)) {
        if (file.pixelWidth) {
          if (file.pixelWidth > maxWidth) badFiles.push(`"${file.name}"`);
        } else {
          const dim = await getImage(file);
          if (dim.width > maxWidth) {
            badFiles.push(`"${file.name}"`);
          }
        }
      }
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.imageMaxWidthValidation, props.errorMessages, `Width of ${badFiles.join(", ")} must be no more than ${maxWidth}px.`),
    validationName: validationNames.imageMaxWidthValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const imageMinHeightValidation = async (value, props, component) => {
  const minHeight = props.minHeight;
  const badFiles = [];
  if (minHeight) {
    for (const file of value) {
      if (shouldValidateImagePixelDimensions(file)) {
        if (file.pixelHeight) {
          if (file.pixelHeight < minHeight) badFiles.push(`"${file.name}"`);
        } else {
          const dim = await getImage(file);
          if (dim.height < minHeight) {
            badFiles.push(`"${file.name}"`);
          }
        }
      }
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.imageMinHeightValidation, props.errorMessages, `Height of ${badFiles.toString().replace(/,/g, ", ")} must be at least ${minHeight}px.`),
    validationName: validationNames.imageMinHeightValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const imageMaxHeightValidation = async (value, props, component) => {
  const maxHeight = props.maxHeight;
  const badFiles = [];
  if (maxHeight) {
    for (const file of value) {
      if (shouldValidateImagePixelDimensions(file)) {
        if (file.pixelHeight) {
          if (file.pixelHeight > maxHeight) badFiles.push(`"${file.name}"`);
        } else {
          const dim = await getImage(file);
          if (dim.height > maxHeight) {
            badFiles.push(`"${file.name}"`);
          }
        }
      }
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.imageMaxHeightValidation, props.errorMessages, `Height of ${badFiles.toString().replace(/,/g, ", ")} must be no more than ${maxHeight}px.`),
    validationName: validationNames.imageMaxHeightValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const imageAspectRatioValidation = async (value, props, component) => {
  const aspectRatio = props.aspectRatio;
  const badFiles = [];
  const gcd = (a, b) => {
    if (!b) {
      return a;
    }
    return gcd(b, a % b);
  };
  for (const file of value) {
    if (shouldValidateImagePixelDimensions(file)) {
      if (file.pixelHeight && file.pixelWidth) {
        const div = gcd(file.pixelWidth, file.pixelHeight);
        const ratioWidth = file.pixelWidth / div;
        const ratioHeight = file.pixelHeight / div;
        if (aspectRatio?.width !== ratioWidth || aspectRatio.height !== ratioHeight) {
          badFiles.push(`"${file.name}"`);
        }
      } else {
        const dim = await getImage(file);
        const div = gcd(dim.width, dim.height);
        const ratioWidth = dim.width / div;
        const ratioHeight = dim.height / div;
        if (aspectRatio?.width !== ratioWidth || aspectRatio.height !== ratioHeight) {
          badFiles.push(`"${file.name}"`);
        }
      }
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.imageAspectRatioValidation, props.errorMessages, `Aspect ratio of ${badFiles.toString().replace(/,/g, ", ")} is not a ${aspectRatio?.width}:${aspectRatio?.height} image.`),
    validationName: validationNames.imageMaxHeightValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const mustBeImageValidation = async (value, props, component) => {
  const mustBeImage = props.mustBeImage;
  const badFiles = [];
  if (mustBeImage) {
    for (const file of value) {
      if (!file.isImage) badFiles.push(`"${file.name}"`);
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.mustBeImageValidation, props.errorMessages, `${badFiles.join(", ")} ${badFiles.length > 1 ? "must be images." : "must be an image."}`),
    validationName: validationNames.mustBeImageValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const minSizeValidation = async (value, props, component) => {
  const minSize = props.minSize;
  const badFiles = [];
  if (minSize === undefined) {
    return Promise.resolve({
      error: false,
      message: undefined,
      validationName: validationNames.minSizeValidation
    });
  }
  for (const file of value) {
    if (minSize && file.fileSize && file.fileSize < minSize) {
      badFiles.push(`"${file.name}"`);
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.minSizeValidation, props.errorMessages, `${badFiles.join(", ")} ${badFiles.length > 1 ? "are" : "is"} less than the minimum size of ${Math.round(minSize * 100.0 / 1000) / 100}KB.`),
    validationName: validationNames.minSizeValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const maxSizeValidation = async (value, props, component) => {
  const maxSize = props.maxSize;
  const badFiles = [];
  if (maxSize === undefined) {
    return Promise.resolve({
      error: false,
      message: undefined,
      validationName: validationNames.maxSizeValidation
    });
  }
  for (const file of value) {
    if (maxSize && file.fileSize && file.fileSize > maxSize) {
      badFiles.push(`"${file.name}"`);
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: errorMessage(validationNames.maxSizeValidation, props.errorMessages, `${badFiles.join(", ")} ${badFiles.length > 1 ? "are" : "is"} greater than the maximum size of ${Math.round(maxSize * 100.0 / 1000) / 100}KB.`),
    validationName: validationNames.maxSizeValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const rejectedFilesValidation = async (value, props, component) => {
  const defaultMessages = [];
  if (Array.isArray(props.filesRejected)) {
    for (const index in props.filesRejected) {
      const file = props.filesRejected[index].file;
      if (props.maxSize && file.size > props.maxSize) {
        defaultMessages.push(`${file.name} is greater than the maximum size of ${Math.round(props.maxSize / 1000)}KB`);
      }
      if (props.minSize && file.size < props.minSize) {
        defaultMessages.push(`${file.name} is less than the minimum size of ${Math.round(props.minSize / 1000)}KB`);
      }
      if (props.fileTypes && !props.fileTypes.includes(file.type)) {
        const friendlyFileTypes = Array.isArray(props.fileTypes) ? props.fileTypes.join(", ") : props.fileTypes;
        defaultMessages.push(`${file.name} does not match the expected file extensions of ${friendlyFileTypes}`);
      }
      if (props.mustBeImage && !file.type.includes("image")) {
        defaultMessages.push(`${file.name} is not an image file.`);
      }
    }
  }
  const result = {
    error: Array.isArray(props.filesRejected) && props.filesRejected.length > 0,
    message: errorMessage(validationNames.rejectedFilesValidation, props.errorMessages, defaultMessages.join("  |  ")),
    validationName: validationNames.imageMaxHeightValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const referenceMaxItems = async (value, props, component) => {
  const result = {
    error: value && value.length > component.props.maxItems,
    message: `Records exceeded maximum allowed of (${component.props.maxItems})`,
    validationName: validationNames.referenceMaxItems
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const referenceMinItems = async (value, props, component) => {
  const result = {
    error: value && value.length < component.props.minItems,
    message: `Records are less than the minimum requirement of (${component.props.minItems}).`,
    validationName: validationNames.referenceMinItems
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const allowedExtensionsValidation = async (value, props, component) => {
  const fileTypes = props.fileTypes;
  const badFiles = [];
  if (fileTypes) {
    for (const file of value) {
      if (file.fileExtension && !fileTypes.includes(file.fileExtension)) badFiles.push(`"${file.name}"`);
    }
  }
  const result = {
    error: badFiles.length > 0,
    message: `Only the following files types are allowed: ${fileTypes}`,
    validationName: validationNames.allowedExtensionsValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const geoLocationRequireValidation = (value, props, component) => {
  const message = [];
  let hasError = false;
  if (props.fields?.showAddress1 && props.validateFields?.validateAddress1 && !value.address1) {
    message.push("Street Address");
  }
  if (props.fields?.showCity && props.validateFields?.validateCity && !value.city) {
    message.push("City");
  }
  if (props.fields?.showState && (props.validateFields?.validateState ?? true) && !value.state) {
    message.push("State");
  }
  if (props.fields?.showZipCode && (props.validateFields?.validateZipCode ?? true) && !value.zip) {
    message.push("ZIP code");
  }
  let formattedErrorMessage = message.join(", ");
  if (message.length > 1) {
    const replacement = " and";
    formattedErrorMessage = formattedErrorMessage.replace(/,([^,]*)$/, replacement + "$1");
  }
  hasError = !value || message.length > 0;
  const result = {
    error: hasError,
    message: hasError ? errorMessage(validationNames.required, props.errorMessages, `${formattedErrorMessage} is required.`) : "",
    validationName: validationNames.required
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const childComponentValidation = async () => {
  const results = [];
  const hasError = false;
  const result = {
    error: hasError ? results : false,
    message: "",
    validationName: validationNames.childComponentValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getValue = (value, component) => {
  return value?.value ?? value ?? component?.props?.value;
};
export const recurrenceValidation = (value, props, component) => {
  let error = !props.allowRecurring && !value;
  let message = "These fields cannot be blank.";
  const recurrence = getValue(value, component)?.recurrence;
  // If recurrence is being used, check its fields
  if (recurrence && recurrence.type !== recurrenceTypes.NONE) {
    // falsy check for occurrences is ok because its a string value
    if (!recurrence.until && !recurrence.occurrences) {
      error = true;
    }
    if (!error && !recurrence.until && recurrence.occurrences && recurrence.occurrences < 1) {
      error = true;
      message = "The number of occurrences must be greater than 0.";
    }
    // The interval must be above 1, anything below that doesn't make sense.
    if (!recurrence.interval) {
      error = true;
    }
    if (!error && recurrence.interval < 1) {
      error = true;
      message = "The interval must be greater than 0.";
    }

    // WEEKLY
    if (recurrence.type === recurrenceTypes.WEEKLY) {
      // Weekdays must be selected.
      if (!recurrence.weekdays || recurrence.weekdays.length === 0) {
        error = true;
        message = "Weekdays must be selected.";
      }
    }

    //MONTHLY
    if (recurrence.type === recurrenceTypes.MONTHLY) {
      // if offset is empty, this means the toggle is set to repeatOn so there should be a dayOfMonth.
      if (!recurrence.dayOfMonth && !recurrence.offset || recurrence.dayOfMonth && recurrence.dayOfMonth < 1 || recurrence.dayOfMonth > 31) {
        error = true;
        message = '"Each" field must be a valid day of the month.';
      }
    }
  }
  const result = {
    error: error,
    message: errorMessage(validationNames.required, props.errorMessages, message),
    validationName: validationNames.recurrenceValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const linkFieldValidation = (value, props, component) => {
  const result = {
    error: Boolean(props.required) && !value.url,
    message: errorMessage(validationNames.required, props.errorMessages, "This field cannot be blank."),
    validationName: validationNames.required
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const wrappedFieldRendererValidation = async (value, props, component) => {
  const isValid = await component.handleValidation();
  const result = {
    error: !isValid,
    message: "",
    validationName: validationNames.wrappedFieldRendererValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const specialCharactersValidation = (value, props, component) => {
  const result = {
    error: hasSpecialCharacters(value),
    message: errorMessage(validationNames.required, props.errorMessages, "This field cannot include special characters."),
    validationName: validationNames.specialCharactersValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
export const emailValidation = (value, props, component) => {
  const result = {
    error: !isValidEmail(value),
    message: errorMessage(validationNames.required, props.errorMessages, "This field must be a valid Email Address."),
    validationName: validationNames.emailValidation
  };
  return result.error ? Promise.reject(result) : Promise.resolve(result);
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function enhanceWithValidation(WrappedComponent) {
  var _ValidationComponent;
  return _ValidationComponent = class ValidationComponent extends React.Component {
    constructor(props) {
      super(props);
      _defineProperty(this, "wrappedComponentRef", void 0);
      _defineProperty(this, "onBlur", e => {
        if (this.props.onlyValidateOnBlur && !this.props.doNotValidate) {
          this.validate();
        }
        this.props.onBlur && this.props.onBlur(e);
      });
      _defineProperty(this, "validate", async value => {
        // doNotValidate prop does not go here, but in the functions consuming it.
        // This way, the validate function can be called directly within the component, and still work.
        const {
          validations,
          ...others
        } = this.props;
        value = typeof value === "undefined" ? this.props.value : value;
        const messages = [];
        const messagesFrom = [];
        const messagesTo = [];
        const validationNames = [];
        if (validations?.length) {
          for (const validation of validations) {
            try {
              await validation(value, {
                ...others
              }, this.wrappedComponentRef.current);
            } catch (result) {
              if (result && typeof result === "object") {
                const typedResult = result;
                if (typedResult.messageFrom) messagesFrom.push(typedResult.messageFrom);
                if (typedResult.messageTo) messagesTo.push(typedResult.messageTo);
                if (typedResult.message) messages.push(typedResult.message);
                if (typedResult.validationName) validationNames.push(typedResult.validationName);
              }
            }
          }
        }
        let result;
        if (messagesFrom.length === 0 && messagesTo.length === 0) {
          result = messages.length > 0 ? {
            error: true,
            message: messages.join(" "),
            validationName: validationNames.join(", ")
          } : {
            error: false,
            message: "",
            validationName: null
          };
        } else {
          result = {
            error: {
              from: false,
              to: false
            },
            message: {
              from: messagesFrom.join(" "),
              to: messagesTo.join(" ")
            },
            validationName: validationNames.join(", ")
          };
          if (result.message) {
            if (messages.length > 0) {
              result.message["from"] += " " + messages.join(" ");
              result.message["to"] += " " + messages.join(" ");
            }
            if (result.message["from"].length > 0) result.error["from"] = true;
            if (result.message["to"].length > 0) result.error["to"] = true;
          }
        }
        this.setState(result);
        if (typeof this.props.onValidation === "function") {
          this.props.onValidation(result);
        }
        return result;
      });
      this.state = {
        error: false,
        message: "",
        validationName: ""
      };
      this.wrappedComponentRef = /*#__PURE__*/React.createRef();
    }
    async componentDidMount() {
      if (WrappedComponent.displayName === "DateTimeRange") {
        const value = this.props.value;
        if (value?.from === null && value?.to === null) return;
      }
      const haveValue = this.props.value || this.props.zeroIsValidValue && this.props.value === 0;
      if (haveValue && !this.props.doNotValidate && !this.props.onlyValidateOnBlur) {
        await this.validate();
      }
      return Promise.resolve();
    }
    componentDidUpdate(prevProps, prevState) {
      if (this.props.onlyValidateOnBlur || this.props.doNotValidate) {
        return;
      }
      if (!deepEquals(this.props.value, prevProps.value)) {
        this.validate();
      } else if (this.props.filesRejected && !deepEquals(this.props.filesRejected, prevProps.filesRejected)) {
        this.validate();
      }
    }
    render() {
      const {
        regExpPattern,
        errorMessages,
        validations,
        onValidation,
        onlyValidateOnBlur,
        minLength,
        maxLength,
        minValue,
        maxValue,
        ...otherProps
      } = this.props;
      delete otherProps["data-maxLength"];
      delete otherProps["data-minLength"];
      const dateFieldProps = WrappedComponent.displayName === "DateTimeRange" ? {
        minValue,
        maxValue
      } : undefined;
      return /*#__PURE__*/React.createElement(WrappedComponent, _extends({
        ref: this.wrappedComponentRef
      }, otherProps, dateFieldProps, {
        onBlur: this.onBlur,
        executeValidation: this.validate,
        error: this.state.error || this.props.error,
        errorMessage: this.state.message
      }));
    }
  }, _defineProperty(_ValidationComponent, "propTypes", validationPropTypes), _defineProperty(_ValidationComponent, "defaultProps", {
    error: false
  }), _ValidationComponent;
}
export default enhanceWithValidation;